import {
  EmploymentSalaryRow,
  GroupedEmploymentSalaryRow,
  SalaryIncreaseStatus,
  SalaryWarningType,
} from "../../types/common";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import SalaryTableCells from "./SalaryTableCells";
import {
  formatTesSalaryTableSelectionSummary,
  isGroupedEmploymentSalaryRow,
  renderRowStatus,
  uiRowEmpToId,
} from "../helpers";
import { Fragment, useState } from "react";
import isEmpty from "../../../../common/helpers/isEmpty";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import BulkSalaryIncreaseCollapsedRows from "./BulkSalaryIncreaseCollapsedRows";
import { format } from "date-fns";
import { leadingZerosDateFormat } from "../../../../common/helpers/formatDateTime";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  UnfoldLessSharp,
  UnfoldMoreSharp,
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Trans } from "react-i18next";
import { MassUpdateStatusRowId } from "../../types/queries";

type Props = {
  row: GroupedEmploymentSalaryRow | EmploymentSalaryRow;
  index: number;
  raiseDateSalaryKeys: string[];
  afterRaiseDateSalaryPerDateKeys: string[];
  isSelected?: boolean;
  onSelect?: () => void;
  toggleUnclear: (rowId: MassUpdateStatusRowId) => void;
  toggleExpandedRow?: (rowId: MassUpdateStatusRowId) => void;
};

const BulkSalaryIncreaseEmploymentRow = ({
  row,
  index,
  raiseDateSalaryKeys,
  afterRaiseDateSalaryPerDateKeys,
  isSelected,
  onSelect,
  toggleUnclear,
  toggleExpandedRow
}: Props) => {
  const [open, setOpen] = useState(false);

  const mainRowBackground = index % 2 === 0 ? "#fff" : "#FAFAFB";
  const setRowClearance = (row: EmploymentSalaryRow) => {
    // console.log(`row ${uiRowEmpToId(row)} is now unclear ${row.unclear}`)
    //toggleUnclear(uiRowEmpToId(row));
    toggleUnclear(row.id);
  }

  const isGrouped = isGroupedEmploymentSalaryRow(row);
  const expanded = isGrouped && row.expanded;
  // console.log("grouped?", isGrouped, expanded, row);

  const warningText = (
    <Box>
      {row.warnings?.map((w, idx) => (
        <Trans
          key={`${w.type}-${idx}`}
          i18nKey={`warnings.${w.type}`}
          ns="massSalaryIncrease"
          values={w.params}
        />
      ))}
    </Box>
  );
  const errorIconColor = row.warnings?.find(w => w.type === SalaryWarningType.INCOMPATIBLE_SALARY_COMPONENTS) ? "error" : "warning";

  const formatDate = (row: EmploymentSalaryRow) => {
    return format(new Date(row.raiseDateSalary.date), leadingZerosDateFormat)
  }

  const tableSelectionText = (salaryRow: EmploymentSalaryRow) =>
    formatTesSalaryTableSelectionSummary(salaryRow.raiseDateSalary);

  const groupedTableNames = isGrouped && !expanded
    ? [...new Set(row.groupedEmploymentRows
      .map((employmentRow) => tableSelectionText(employmentRow))
      .filter(Boolean))]
      .join(", ")
    : null;

  return (
    <Fragment key={row.id}>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: mainRowBackground,
        }}
      >
        <TableCell>
          <Checkbox
            checked={isSelected}
            onChange={onSelect}
            disabled={row.status === SalaryIncreaseStatus.SUCCESS}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <IconButton
              sx={{
                visibility: !isEmpty(row.afterRaiseSalaryPerDate)
                  ? "visible"
                  : "hidden",
              }}
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            {!isEmpty(row.warnings) && (
              <Tooltip title={warningText}>
                <ErrorOutlineIcon color={errorIconColor} fontSize="small" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Button
            // disabled={disabled}
            component={Link}
            target="_blank"
            to={`/home/talents/${row.talentId}/employment/${row.employmentId}`}
            style={{ paddingLeft: "0px", paddingRight: "0px" }}
            // onClick={() => {
            //   dispatch(setContinousEmploymentId(continuousEmploymentId || ""));
            // }}
            endIcon={
              <ArrowRight
                sx={{
                  height: "16px",
                  width: "16px",
                  color: (theme) =>
                    // disabled ? "text.disabled" :
                    theme.palette.primary.main,
                }}
              />
            }
          >
            <Typography variant="header" fontWeight="500" fontSize="12px">
              {/* {t("talentProfile:employmentAdditionalInfo")} */}
              {`${row.firstName} ${row.lastName}`}
            </Typography>
          </Button>
        </TableCell>
          <TableCell>
            {isGrouped && !expanded ?
            // `(${row.groupedEmploymentRows.length}) ${row.groupedEmploymentRows.map(r => r.workTitle.name).join(",\n")}` :
            //   `(${row.groupedEmploymentRows.length}) ${[...new Set(row.groupedEmploymentRows.map(r => r.workTitle.name))].join(",\n")}` :
              `(${row.groupedEmploymentRows.length}) ${row.uniqueWorkTitles}` :
              row.workTitle.name}
          </TableCell>
        <TableCell>
          {isGrouped && !expanded ?
            `(${row.groupedEmploymentRows.length}) ${row.groupedEmploymentRows.map(r => formatDate(r)).join(",\n")}`
            :
            formatDate(row) + " " + row.employmentId
          }
          <Tooltip title={isGrouped && row.expanded ? "Niputa" : "Näytä niputetut rivit"}>
            <IconButton
              sx={{
                visibility: isGrouped
                  ? "visible"
                  : "hidden",
                color: expanded ? "red": "blue"
              }}
              size="small"
              onClick={() => {
                //setExpanded(!expanded);
                toggleExpandedRow?.(row.id);
              }}
            >
              {expanded ? <UnfoldLessSharp/> : <UnfoldMoreSharp/>}
            </IconButton>
          </Tooltip>

        </TableCell>
        <TableCell>{groupedTableNames ?? tableSelectionText(row)}</TableCell>
        <TableCell>{row.raiseDateContEmpExists ? "On" : "Ei"}</TableCell>
        <TableCell>{row.employerName}</TableCell>
        <SalaryTableCells
          row={row}
          index={index}
          salaryKeys={raiseDateSalaryKeys}
        />
        <TableCell>{renderRowStatus(row.status)}</TableCell>
        <TableCell>
          <Tooltip
            title={row.unclear ? "This row is marked as unclear" : "This row is marked as clear"}>
            <Checkbox
              checked={row.unclear}
              onChange={() => setRowClearance(row)}
              disabled={row.status === SalaryIncreaseStatus.SUCCESS}
            />
          </Tooltip>
        </TableCell>
        <TableCell>{row.hrRepresentative?.name ?? null}</TableCell>
      </TableRow>
      <BulkSalaryIncreaseCollapsedRows
        open={open}
        row={row}
        afterRaiseDateSalaryPerDateKeys={afterRaiseDateSalaryPerDateKeys}
      />
    </Fragment>
  );
};

export default BulkSalaryIncreaseEmploymentRow;
